import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer from 'src/components/StyledComponents/StyledGridContainer';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { useGetRawMaterialCategory } from 'src/services/rawMaterialCategory.service';
import { RawMaterialCategory } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import RawMaterialCategoryActionMenuButton from 'src/components/ActionButtons/RawMaterialCategoryActionMenu';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { loadData } from 'src/utils/grid.utils';
import dayjs from 'dayjs';
import { DATE_FORMATS } from 'src/utils/properties.utils';

const RawMaterialCategoryGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const { loading, categories, fetchCategories, deleteCategory, totalItems } =
    useGetRawMaterialCategory();
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'desc' },
  ]);

  useEffect(() => {
    loadData(
      () => fetchCategories(page, pageSize, searchTerm, sortBy, order),
      showSnackbar
    );
  }, [state, pageSize, searchTerm, page, sortBy, order]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const sortBy = newSortModel[0]?.field || 'createdAt';
    const order = (newSortModel[0]?.sort || 'desc').toUpperCase();
    setPage(1);
    setSortBy(sortBy);
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    { field: 'name', headerName: 'Category Name', flex: 1.5 },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      flex: 0.5,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <RawMaterialCategoryActionMenuButton
          rowId={params.id}
          name={params.row.name}
          fetchCategories={() =>
            fetchCategories(page, pageSize, searchTerm, sortBy, order)
          }
          deleteCategory={deleteCategory}
        />
      ),
    },
  ];

  const transformData = (categories: RawMaterialCategory[]) => {
    try {
      return categories.map((category, index) => ({
        id: category.id,
        number: (page - 1) * pageSize + index + 1,
        name: category.name,
        createdAt: dayjs(category.createdAt).format(DATE_FORMATS.DISPLAY),
      }));
    } catch (error) {
      showSnackbar('Error transforming category data', 'error');
      return [];
    }
  };

  const rows = transformData(categories);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        sortModel={sortModel}
        sortingMode="server"
        sortingOrder={['desc', 'asc']}
        onSortModelChange={handleSortModelChange}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default RawMaterialCategoryGrid;
